/**
 * Secure storage for non-extractable CryptoKeys using IndexedDB
 * Keys persist across page reloads but cannot be extracted
 */

class SecureKeyStorage {
    private dbName = 'VasisProSecure';
    private storeName = 'cryptoKeys';
    private db: IDBDatabase | null = null;

    async init(): Promise<void> {
        if (this.db) return;

        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, 1);

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                if (!db.objectStoreNames.contains(this.storeName)) {
                    db.createObjectStore(this.storeName, { keyPath: 'id' });
                }
            };

            request.onsuccess = (event) => {
                this.db = (event.target as IDBOpenDBRequest).result;
                resolve();
            };

            request.onerror = () => reject(request.error);
        });
    }

    async getOrCreateHeartbeatKey(): Promise<CryptoKey> {
        await this.init();

        // Try to retrieve existing key
        const record = await this.getKey('heartbeat-key');
        if (record) {
            console.log('✅ Retrieved heartbeat key from IndexedDB');
            return record;
        }

        // Key doesn't exist - will be created after server sends secret
        // This happens on first heartbeat
        console.log('⏳ No heartbeat key found - will be enrolled on first heartbeat');
        throw new Error('ENROLLMENT_REQUIRED');
    }

    async importHeartbeatKeyFromSecret(secretBase64: string): Promise<CryptoKey> {
        await this.init();

        // Convert Base64 secret to bytes
        const secretBytes = Uint8Array.from(atob(secretBase64), c => c.charCodeAt(0));

        // Import as non-extractable HMAC key
        const key = await crypto.subtle.importKey(
            'raw',
            secretBytes,
            { name: 'HMAC', hash: 'SHA-256' },
            false,  // Non-extractable - CRITICAL for security
            ['sign']
        );

        // Store in IndexedDB
        await this.storeKey('heartbeat-key', key);
        console.log('✅ Imported and stored heartbeat key from server');
        return key;
    }


    private async getKey(id: string): Promise<CryptoKey | null> {
        return new Promise((resolve, reject) => {
            const tx = this.db!.transaction(this.storeName, 'readonly');
            const store = tx.objectStore(this.storeName);
            const req = store.get(id);

            req.onsuccess = () => resolve(req.result?.key || null);
            req.onerror = () => reject(req.error);
        });
    }

    private async storeKey(id: string, key: CryptoKey): Promise<void> {
        return new Promise((resolve, reject) => {
            const tx = this.db!.transaction(this.storeName, 'readwrite');
            const store = tx.objectStore(this.storeName);
            const req = store.put({ id, key });

            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }

    async clearHeartbeatKey(): Promise<void> {
        await this.init();
        return new Promise((resolve, reject) => {
            const tx = this.db!.transaction(this.storeName, 'readwrite');
            const store = tx.objectStore(this.storeName);
            const req = store.delete('heartbeat-key');

            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }
}

export const keyStorage = new SecureKeyStorage();
